import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { formatDurationMonths, formatMoney } from '../utils/format.js';
import { FindCourseContext } from './FindCourseContext.js';
import { useLeadModal } from './LeadModalContext.js';

export { useFindCourse } from './FindCourseContext.js';

const LEVEL_LABEL = { UG: 'undergraduate', PG: 'postgraduate', Diploma: 'diploma / certificate' };

const DOMAIN_STEPS = [
  { id: 'business', question: 'Are you interested in business or management?', hint: 'MBA, BBA and similar programmes.' },
  { id: 'computers', question: 'Are you interested in computers or IT?', hint: 'MCA, BCA and similar programmes.' },
  { id: 'commerce', question: 'Are you interested in commerce or accounting?', hint: 'M.Com, B.Com and similar programmes.' }
];

function levelOf(answers) {
  if (answers.completed12 === false) return 'Diploma';
  return answers.hasBachelors ? 'PG' : 'UG';
}

/** The questions to ask, given the answers so far. Order matters. */
function buildSteps(answers, caps) {
  const steps = [
    {
      id: 'completed12',
      question: 'Have you completed Class 12 (or an equivalent)?',
      hint: 'If not, we will show diploma and certificate options.'
    }
  ];
  if (answers.completed12 !== false) {
    steps.push({
      id: 'hasBachelors',
      question: "Do you already hold a bachelor's degree?",
      hint: 'Yes shows postgraduate programmes, No shows undergraduate programmes.'
    });
    steps.push(...DOMAIN_STEPS);
  }
  const cap = caps?.[levelOf(answers)];
  if (cap) {
    steps.push({
      id: 'budgetConscious',
      question: `Would you like to keep the total programme fee under ${formatMoney(cap, 'INR')}?`,
      hint: 'This is roughly the middle of the fees for this level across listed universities.'
    });
  }
  return steps;
}

function summarise(answers, result) {
  const parts = [`Level: ${result.levelCode}`];
  const interests = Object.entries(answers)
    .filter(([key, value]) => DOMAIN_STEPS.some((s) => s.id === key) && value === true)
    .map(([key]) => key);
  if (interests.length) parts.push(`Interests: ${interests.join(', ')}`);
  if (result.budgetApplied) parts.push(`Budget under ${formatMoney(result.budgetCap, 'INR')}`);
  const picks = result.groups
    .flatMap((group) => group.courses.map((c) => `${c.name} (${group.university.name})`))
    .join('; ');
  return `Find My Course quiz. ${parts.join(' | ')}. Shortlist: ${picks}`.slice(0, 900);
}

export default function FindCourseProvider({ children }) {
  const { openLeadModal, markEngaged } = useLeadModal();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState({});
  const [stepIndex, setStepIndex] = useState(0);
  const [caps, setCaps] = useState(null);
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | loading | error
  const dialogRef = useRef(null);

  const openFindCourse = useCallback(() => {
    markEngaged();
    setAnswers({});
    setStepIndex(0);
    setResult(null);
    setStatus('idle');
    setOpen(true);
  }, [markEngaged]);

  const close = useCallback(() => setOpen(false), []);

  // Budget wording comes from real fee data, so fetch it when the quiz opens.
  useEffect(() => {
    if (!open || caps) return undefined;
    let cancelled = false;
    fetch('/api/find-course/config')
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('config'))))
      .then((payload) => {
        if (!cancelled) setCaps(payload.data?.budgetCaps || {});
      })
      .catch(() => {
        if (!cancelled) setCaps({});
      });
    return () => {
      cancelled = true;
    };
  }, [open, caps]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    function onKey(event) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const steps = useMemo(() => buildSteps(answers, caps), [answers, caps]);
  const step = steps[stepIndex];

  const submit = useCallback(async (finalAnswers) => {
    setStatus('loading');
    try {
      const body = {
        completed12: finalAnswers.completed12 !== false,
        hasBachelors: finalAnswers.hasBachelors === true,
        domains: DOMAIN_STEPS.filter((s) => finalAnswers[s.id] === true).map((s) => s.id),
        budgetConscious: finalAnswers.budgetConscious === true
      };
      const res = await fetch('/api/find-course', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error('request failed');
      const payload = await res.json();
      setResult(payload.data);
      setStatus('idle');
    } catch {
      setStatus('error');
    }
  }, []);

  function answer(value) {
    const next = { ...answers, [step.id]: value };
    // Changing an earlier answer can change which questions remain.
    const nextSteps = buildSteps(next, caps);
    const keep = new Set(nextSteps.map((s) => s.id));
    const cleaned = Object.fromEntries(Object.entries(next).filter(([key]) => keep.has(key)));
    setAnswers(cleaned);
    if (stepIndex + 1 < nextSteps.length) {
      setStepIndex(stepIndex + 1);
    } else {
      submit(cleaned);
    }
  }

  function back() {
    if (result || status === 'error') {
      setResult(null);
      setStatus('idle');
      return;
    }
    setStepIndex((index) => Math.max(0, index - 1));
  }

  function restart() {
    setAnswers({});
    setStepIndex(0);
    setResult(null);
    setStatus('idle');
  }

  function emailShortlist() {
    const first = result.groups[0]?.courses[0];
    const program = first ? `${first.name} — ${result.groups[0].university.name}` : undefined;
    const notes = summarise(answers, result);
    setOpen(false);
    openLeadModal({
      intent: 'quiz',
      leadSource: 'find_course',
      preferredProgram: program,
      extraPayload: { notes }
    });
  }

  const progress = result ? 100 : Math.round((stepIndex / Math.max(steps.length, 1)) * 100);
  const ready = caps !== null;

  return (
    <FindCourseContext.Provider value={{ openFindCourse }}>
      {children}
      {open && (
        <div className="lead-modal-backdrop" onClick={close} role="presentation">
          <div
            className="lead-modal quiz-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="quiz-title"
            tabIndex={-1}
            ref={dialogRef}
            onClick={(event) => event.stopPropagation()}
          >
            <button type="button" className="lead-modal-close" aria-label="Close" onClick={close}>
              ×
            </button>
            <p className="quiz-eyebrow">Find my course</p>
            <h2 id="quiz-title" className="quiz-title">
              {result ? 'Your shortlist' : 'A few quick questions'}
            </h2>

            <div
              className="quiz-progress"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={progress}
            >
              <span style={{ width: `${progress}%` }} />
            </div>

            {!ready && <p className="quiz-status">Loading…</p>}

            {ready && !result && status === 'idle' && step && (
              <div className="quiz-step">
                <p className="quiz-count">
                  Question {stepIndex + 1} of {steps.length}
                </p>
                <p className="quiz-question">{step.question}</p>
                {step.hint && <p className="quiz-hint">{step.hint}</p>}
                <div className="quiz-answers">
                  <button type="button" className="cta-button" onClick={() => answer(true)}>
                    Yes
                  </button>
                  <button
                    type="button"
                    className="cta-button cta-button-secondary"
                    onClick={() => answer(false)}
                  >
                    No
                  </button>
                </div>
                {stepIndex > 0 && (
                  <button type="button" className="link-button" onClick={back}>
                    ← Back
                  </button>
                )}
              </div>
            )}

            {status === 'loading' && <p className="quiz-status">Finding programmes…</p>}

            {status === 'error' && (
              <div className="quiz-step">
                <p className="quiz-question">We could not build your shortlist.</p>
                <p className="quiz-hint">Please check your connection and try again.</p>
                <div className="quiz-answers">
                  <button type="button" className="cta-button" onClick={() => submit(answers)}>
                    Try again
                  </button>
                </div>
              </div>
            )}

            {result && (
              <div className="quiz-results">
                {result.groups.length === 0 ? (
                  <>
                    <p className="quiz-question">No listed programmes match those answers yet.</p>
                    <p className="quiz-hint">
                      A counsellor can look beyond what is listed on the site.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="quiz-hint">
                      {result.totalMatches} {LEVEL_LABEL[result.levelCode] || ''} programme
                      {result.totalMatches === 1 ? '' : 's'} across {result.universityCount}{' '}
                      universit{result.universityCount === 1 ? 'y' : 'ies'}
                      {result.budgetApplied
                        ? `, with fees under ${formatMoney(result.budgetCap, 'INR')}`
                        : ''}
                      . Here are a few to start with.
                    </p>
                    {result.groups.map((group) => (
                      <div className="quiz-group" key={group.university.slug}>
                        <h3>
                          <Link to={`/universities/${group.university.slug}`} onClick={close}>
                            {group.university.name}
                          </Link>
                        </h3>
                        <ul>
                          {group.courses.map((course) => (
                            <li key={course.slug}>
                              <Link
                                to={`/universities/${group.university.slug}/courses/${course.slug}`}
                                onClick={close}
                              >
                                {course.name}
                              </Link>
                              <span className="quiz-course-meta">
                                {course.totalFeeMin
                                  ? formatMoney(course.totalFeeMin, course.currency)
                                  : 'Fee on request'}
                                {course.durationMonths
                                  ? ` · ${formatDurationMonths(course.durationMonths)}`
                                  : ''}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </>
                )}
                <p className="quiz-disclaimer">
                  Eligibility shown here is indicative. Each university sets its own admission
                  criteria, so please confirm with the university or a counsellor.
                </p>
                <div className="quiz-answers">
                  <button
                    type="button"
                    className="cta-button"
                    onClick={
                      result.groups.length
                        ? emailShortlist
                        : () => {
                            setOpen(false);
                            openLeadModal({ intent: 'counsel', leadSource: 'find_course' });
                          }
                    }
                  >
                    {result.groups.length ? 'Email me this shortlist' : 'Talk to a counsellor'}
                  </button>
                  <button type="button" className="cta-button cta-button-secondary" onClick={restart}>
                    Start over
                  </button>
                </div>
                <button type="button" className="link-button" onClick={back}>
                  ← Change last answer
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </FindCourseContext.Provider>
  );
}
